import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Plus, Pencil, Trash2, LogOut, Package, Tag, Star } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const AdminDashboard = () => {
  const { admin, logout, authHeaders } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND}/api/products`);
      setProducts(data);
    } catch (e) {
      toast.error('Erreur de chargement des produits');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Supprimer "${name}" ?`)) return;
    try {
      await axios.delete(`${BACKEND}/api/products/${id}`, { headers: authHeaders() });
      toast.success('Produit supprimé');
      fetchProducts();
    } catch (e) {
      toast.error('Erreur de suppression');
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Déconnecté');
    navigate('/admin/login');
  };

  const totalValue = products.reduce((s, p) => s + p.price * (p.stock || 0), 0);
  const featuredCount = products.filter((p) => p.is_featured).length;

  return (
    <div className="min-h-screen bg-muted/30 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading font-bold text-4xl text-secondary" data-testid="admin-dashboard-title">
              Tableau de bord Admin
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Connecté: {admin?.email}</p>
          </div>
          <div className="flex gap-3">
            <Link
              to="/admin/produits/nouveau"
              className="inline-flex items-center gap-2 bg-accent text-accent-foreground hover:bg-accent/90 px-5 py-3 rounded-lg font-medium shadow-lg shadow-accent/20"
              data-testid="admin-add-product-btn"
            >
              <Plus className="w-4 h-4" /> Nouveau produit
            </Link>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-2 border border-border text-secondary hover:bg-muted px-5 py-3 rounded-lg font-medium"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4" /> Déconnexion
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white border border-border rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <Package className="w-5 h-5 text-primary" />
              <span className="text-sm text-muted-foreground">Total produits</span>
            </div>
            <p className="text-3xl font-bold text-secondary" data-testid="stat-total-products">{products.length}</p>
          </div>
          <div className="bg-white border border-border rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <Tag className="w-5 h-5 text-primary" />
              <span className="text-sm text-muted-foreground">Valeur du stock</span>
            </div>
            <p className="text-3xl font-bold text-secondary">{totalValue.toFixed(0)} DH</p>
          </div>
          <div className="bg-white border border-border rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <Star className="w-5 h-5 text-accent" />
              <span className="text-sm text-muted-foreground">Produits en vedette</span>
            </div>
            <p className="text-3xl font-bold text-secondary">{featuredCount}</p>
          </div>
        </div>

        {/* Products table */}
        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-border">
            <h2 className="font-heading font-semibold text-xl text-secondary">Produits</h2>
          </div>
          {loading ? (
            <div className="p-12 text-center text-muted-foreground">Chargement…</div>
          ) : products.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">Aucun produit. Ajoutez-en un !</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left text-xs font-mono uppercase tracking-wide text-muted-foreground px-6 py-3">Produit</th>
                    <th className="text-left text-xs font-mono uppercase tracking-wide text-muted-foreground px-6 py-3">Catégorie</th>
                    <th className="text-left text-xs font-mono uppercase tracking-wide text-muted-foreground px-6 py-3">Prix</th>
                    <th className="text-left text-xs font-mono uppercase tracking-wide text-muted-foreground px-6 py-3">Réduction</th>
                    <th className="text-left text-xs font-mono uppercase tracking-wide text-muted-foreground px-6 py-3">Stock</th>
                    <th className="text-right text-xs font-mono uppercase tracking-wide text-muted-foreground px-6 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id} className="border-t border-border hover:bg-muted/20" data-testid={`admin-product-row-${p.id}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <img src={p.image_url} alt={p.name} className="w-12 h-12 rounded-lg object-cover" />
                          <div>
                            <p className="font-medium text-secondary">{p.name}</p>
                            {p.brand && <p className="text-xs text-muted-foreground">{p.brand}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-secondary">{p.category}</td>
                      <td className="px-6 py-4 font-mono text-sm font-semibold text-primary">{p.price.toFixed(2)} DH</td>
                      <td className="px-6 py-4">
                        {p.discount_percentage > 0 ? (
                          <span className="inline-flex items-center bg-accent/10 text-accent font-semibold text-xs px-2 py-1 rounded-full">
                            -{p.discount_percentage}%
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-secondary">{p.stock}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <Link
                            to={`/admin/produits/${p.id}`}
                            className="p-2 hover:bg-muted rounded-lg text-secondary"
                            data-testid={`admin-edit-btn-${p.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Link>
                          <button
                            onClick={() => handleDelete(p.id, p.name)}
                            className="p-2 hover:bg-red-50 rounded-lg text-red-600"
                            data-testid={`admin-delete-btn-${p.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
